import { forwardRef } from 'react'
import type { SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'

export type SelectSize = 'xs' | 'sm' | 'md'

/** Padding/typography per size — mirrors the project's input sizes. */
const SIZES: Record<SelectSize, { select: string; chevron: number }> = {
  xs: { select: 'pl-2 pr-6 py-1.5 text-xs', chevron: 12 },
  sm: { select: 'pl-2 pr-7 py-1.5 text-sm', chevron: 14 },
  md: { select: 'pl-3 pr-8 py-2 text-sm', chevron: 15 },
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Visual size: xs = compact rows, sm = toolbars/forms (default), md = detail-panel fields. */
  size?: SelectSize
  /** Layout classes for the wrapper (w-full, flex-1, min-w-0, …). */
  className?: string
}

/**
 * Styled native <select> — white background, project border/focus ring and a
 * lucide chevron instead of the platform arrow. A thin wrapper: all regular
 * select props pass straight through.
 */
const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'sm', className, children, ...props },
  ref,
) {
  return (
    <div className={cn('relative', className)}>
      <select
        ref={ref}
        {...props}
        className={cn(
          'w-full h-full appearance-none bg-white border border-gray-200 rounded-lg text-brand-dark',
          'focus:outline-none focus:ring-2 focus:ring-brand-primary/40',
          'disabled:bg-gray-50 disabled:text-gray-400',
          SIZES[size].select,
        )}
      >
        {children}
      </select>
      <ChevronDown
        size={SIZES[size].chevron}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
      />
    </div>
  )
})

export default Select
